import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, ImageIcon, EyeOff, Eye, Trash2 } from "lucide-react";
import { money, priceView } from "@/lib/api";
import { ConditionBadge, MarketingBadge } from "./Badges";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export const ProductCard = ({ product, testIdPrefix = "product", admin = false, onEditPhotos, onToggleHide, onRemove }) => {
  const { addItem, toggleLocalWishlist, isInLocalWishlist } = useCart();
  const pv = priceView(product);
  const pct = pv.pct;
  const outOfStock = (product.quantity || 0) <= 0;
  const wished = isInLocalWishlist(product.id);
  const hidden = !!product.hidden;

  const onAdd = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const r = await addItem(product);
    if (r.ok) toast.success("Added to cart", { description: product.product_name });
    else toast.error(r.message);
  };
  const onWishlist = (e) => {
    e.preventDefault(); e.stopPropagation();
    toggleLocalWishlist(product.id);
    toast(wished ? "Removed from wishlist" : "Saved to wishlist");
  };
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn && fn(product); };

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`${testIdPrefix}-card-${product.id}`}
      className={`group relative flex flex-col bg-white rounded-xl border shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden ${hidden ? "border-dashed border-slate-300 opacity-60" : "border-slate-200"}`}
    >
      {admin && (
        <div className="absolute top-0 inset-x-0 z-20 flex flex-wrap items-center gap-1.5 p-2 bg-gradient-to-b from-slate-900/70 to-transparent" data-testid={`admin-strip-${product.id}`}>
          <button
            onClick={stop(onEditPhotos)}
            data-testid={`admin-edit-photos-${product.id}`}
            className="shrink-0 inline-flex items-center gap-1 bg-white/95 text-brand-navy text-xs font-bold rounded-full px-2.5 h-8 shadow hover:bg-white active:scale-95 transition"
          >
            <ImageIcon className="h-3.5 w-3.5" /> Edit photos
          </button>
          <button
            onClick={stop(onToggleHide)}
            data-testid={`admin-toggle-hide-${product.id}`}
            className={`shrink-0 inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 h-8 shadow active:scale-95 transition ${hidden ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-white/95 text-brand-red hover:bg-white"}`}
          >
            {hidden ? <><Eye className="h-3.5 w-3.5" /> Show</> : <><EyeOff className="h-3.5 w-3.5" /> Hide</>}
          </button>
          <button
            onClick={stop(onRemove)}
            data-testid={`admin-remove-${product.id}`}
            title="Permanently delete this product (cannot be undone)"
            className="shrink-0 sm:ml-auto inline-flex items-center gap-1 bg-brand-red text-white text-xs font-bold rounded-full px-2.5 h-8 shadow hover:bg-red-700 active:scale-95 transition"
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      )}
      <div className="relative aspect-square bg-slate-50 overflow-hidden">
        {product.featured_image ? (
          <img
            src={product.featured_image}
            alt={product.product_name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-slate-100 text-slate-400" data-testid={`no-image-${product.id}`}>
            <ImageIcon className="h-8 w-8" />
            <span className="text-xs font-bold uppercase tracking-wider">Image coming soon</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {admin && hidden && (
            <span data-testid={`hidden-badge-${product.id}`} className="bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full inline-flex items-center gap-1 mt-10">
              <EyeOff className="h-3 w-3" /> Hidden
            </span>
          )}
          {pv.onSale && (
            <span data-testid={`sale-badge-${product.id}`} className="bg-brand-red text-white text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full">Sale</span>
          )}
          <ConditionBadge condition={product.condition} />
          {product.badges?.slice(0, 2).map((b) => <MarketingBadge key={b} badge={b} />)}
        </div>
        {pct !== null && pct > 0 && (
          <div className="absolute top-3 right-3 bg-brand-red text-white rounded-full h-12 w-12 flex items-center justify-center font-black text-sm shadow-lg shadow-red-500/30">
            -{pct}%
          </div>
        )}
        <button
          onClick={onWishlist}
          data-testid={`wishlist-toggle-${product.id}`}
          aria-label="Toggle wishlist"
          className="absolute bottom-3 right-3 bg-white/95 backdrop-blur border border-slate-200 rounded-full p-2 shadow hover:shadow-md active:scale-95 transition"
        >
          <Heart className={`h-4 w-4 ${wished ? "fill-brand-red text-brand-red" : "text-slate-700"}`} />
        </button>
        {outOfStock && (
          <div className="absolute inset-0 bg-slate-950/60 flex items-center justify-center">
            <span className="text-white font-heading text-lg">SOLD OUT</span>
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col gap-2 flex-1">
        {product.brand && <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{product.brand}</div>}
        <h3 className="text-sm font-bold text-slate-900 line-clamp-2 min-h-[2.5rem]">{product.product_name}</h3>
        <div className="mt-auto flex items-baseline gap-2 flex-wrap">
          <span className={`text-lg font-heading ${pv.onSale ? "text-brand-red" : "text-brand-navy"}`}>{money(pv.now)}</span>
          {pv.was && pv.was > pv.now && (
            <span className="text-sm text-slate-400 line-through">{money(pv.was)}</span>
          )}
        </div>
        {pv.onSale && pv.retail && <div className="text-xs text-slate-400">Retail <span className="line-through">{money(pv.retail)}</span></div>}
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className={`text-xs font-semibold ${outOfStock ? "text-slate-400" : (product.quantity <= 5 ? "text-brand-red" : "text-emerald-600")}`}>
            {outOfStock ? "Out of stock" : (product.quantity <= 5 ? `Only ${product.quantity} left` : "In stock")}
          </span>
          <button
            onClick={onAdd}
            disabled={outOfStock}
            data-testid={`add-to-cart-${product.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white text-xs font-bold px-3 py-2 hover:bg-brand-red disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition"
          >
            <ShoppingCart className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </div>
    </Link>
  );
};
